import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { SERVICE_DETAIL, COMPANY } from "@/lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const s = SERVICE_DETAIL[slug];
  if (!s) return <Navigate to="/services" replace />;

  return (
    <>
      <section className="relative overflow-hidden bg-brand-blue noise-overlay">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white">
            <Icon name="ArrowRight" size={15} weight="bold" className="rotate-180" /> All Services
          </Link>
          <div className="mt-6 flex items-start gap-5">
            <span className="hidden h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-teal/20 text-brand-teal2 sm:flex">
              <Icon name={s.icon} size={36} weight="duotone" />
            </span>
            <div>
              <h1 className="max-w-3xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">{s.hero}</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">{s.intro}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl">What we provide</h2>
              <p className="mt-5 text-slate-600 md:text-lg">{s.body}</p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-brand-bg px-4 py-3.5 text-sm font-medium text-slate-700">
                    <Icon name="CheckCircle" size={20} weight="fill" className="mt-0.5 shrink-0 text-brand-teal" /> {p}
                  </li>
                ))}
              </ul>
            </div>

            <Reveal delay={0.1}>
              <aside className="rounded-2xl border border-slate-200 bg-brand-bg p-7 lg:sticky lg:top-24">
                <h3 className="font-heading text-lg font-semibold text-slate-900">Ready to get started?</h3>
                <p className="mt-2 text-sm text-slate-600">Request a ride or speak with our team about availability and scheduling.</p>
                <Button data-testid="service-book" onClick={() => navigate("/book-a-ride")}
                  className="mt-5 h-12 w-full rounded-full bg-brand-purple font-semibold text-white hover:bg-brand-purple2">
                  Book a Ride
                </Button>
                <a href={COMPANY.phoneHref}>
                  <Button data-testid="service-call" variant="outline" className="mt-3 h-12 w-full rounded-full border-brand-blue font-semibold text-brand-blue hover:bg-brand-blue hover:text-white">
                    <Icon name="Phone" size={17} weight="fill" className="mr-2" /> {COMPANY.phone}
                  </Button>
                </a>
                {slug === "odp-transportation" && (
                  <p className="mt-5 rounded-xl bg-white p-4 text-xs leading-relaxed text-slate-500">
                    For individuals, families, supports coordinators, agencies, and providers interested in transportation
                    services, contact CareDeserved to discuss availability and service needs.
                  </p>
                )}
              </aside>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABanner />
    </>
  );
}
